// src/views/portal/PortalSessoesForm.jsx
// Lista sessões ativas do usuário e permite encerrar individualmente
// ou todas-exceto-a-atual.
import React, { useEffect, useState, useCallback } from "react";
import portalService from "../../services/portalService";
import Banner from "../../components/ui/Banner";
import ConfirmModal from "../../components/ui/ConfirmModal";

function fmtRelativo(d) {
  if (!d) return "—";
  const dt = new Date(d);
  const diff = Date.now() - dt.getTime();
  if (diff < 0) return dt.toLocaleString("pt-BR");
  const min = Math.floor(diff / 60_000);
  if (min < 1)   return "agora há pouco";
  if (min < 60)  return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)    return `há ${h}h`;
  const d2 = Math.floor(h / 24);
  if (d2 < 30)   return `há ${d2} dia${d2 > 1 ? "s" : ""}`;
  return dt.toLocaleString("pt-BR");
}

function iconeDoDevice(label) {
  const s = String(label || "").toLowerCase();
  if (!s) return "bi-display";
  if (s.includes("android") || s.includes("ios") || s.includes("iphone")) return "bi-phone";
  if (s.includes("mac"))     return "bi-laptop";
  if (s.includes("windows")) return "bi-pc-display";
  if (s.includes("linux"))   return "bi-pc-display";
  return "bi-display";
}

export default function PortalSessoesForm() {
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmRevogar, setConfirmRevogar] = useState(null); // sessao ou "outras"

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await portalService.listarSessoes();
      setSessoes(r.data || []);
    } catch (e) {
      setMsg({ tipo:"danger", texto: e?.message || "Erro ao carregar sessões." });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const revogarUma = async (s) => {
    setLoading(true); setMsg(null);
    try {
      await portalService.revogarSessao(s.id);
      setMsg({ tipo:"success", texto:`Sessão "${s.deviceLabel || "—"}" encerrada.` });
      await carregar();
    } catch (e) {
      setMsg({ tipo:"danger", texto: e?.message || "Erro ao encerrar sessão." });
    } finally {
      setLoading(false);
      setConfirmRevogar(null);
    }
  };

  const revogarOutras = async () => {
    setLoading(true); setMsg(null);
    try {
      const r = await portalService.revogarOutrasSessoes();
      setMsg({ tipo:"success", texto: r?.message || "Outras sessões encerradas." });
      await carregar();
    } catch (e) {
      setMsg({ tipo:"danger", texto: e?.message || "Erro." });
    } finally {
      setLoading(false);
      setConfirmRevogar(null);
    }
  };

  const outras = sessoes.filter(s => !s.atual);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:13,color:"var(--gray-500)"}}>
        Estes são os dispositivos onde sua conta está logada agora. Se você não reconhece algum, encerre a sessão.
      </div>

      {msg && <Banner tipo={msg.tipo} onClose={()=>setMsg(null)}>{msg.texto}</Banner>}

      {loading && !sessoes.length ? (
        <div style={{textAlign:"center",padding:20,color:"var(--gray-500)",fontSize:13}}>
          <span className="spinner-border spinner-border-sm me-2"/>
          Carregando sessões...
        </div>
      ) : sessoes.length === 0 ? (
        <div style={{textAlign:"center",padding:20,color:"var(--gray-400)",fontSize:13}}>
          Nenhuma sessão ativa encontrada.
        </div>
      ) : (
        <>
          {outras.length > 0 && (
            <button type="button" className="btn btn-outline-danger btn-sm"
              disabled={loading}
              onClick={()=>setConfirmRevogar("outras")}
              style={{alignSelf:"flex-start"}}>
              <i className="bi bi-box-arrow-right me-1"/>
              Encerrar outras sessões ({outras.length})
            </button>
          )}

          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {sessoes.map(s => (
              <div key={s.id}
                style={{
                  padding:"12px 14px",
                  background: s.atual ? "#EFF6FF" : "#F8FAFC",
                  border:`1px solid ${s.atual ? "#BFDBFE" : "#E2E8F0"}`,
                  borderRadius:10,
                  display:"flex",alignItems:"center",gap:12,
                }}>
                <i className={`bi ${iconeDoDevice(s.deviceLabel)}`}
                  style={{fontSize:22,color: s.atual ? "#1D4ED8" : "#64748B"}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>
                      {s.deviceLabel || "Dispositivo desconhecido"}
                    </span>
                    {s.atual && (
                      <span style={{
                        fontSize:10,fontWeight:700,padding:"2px 8px",
                        background:"#1D4ED8",color:"#fff",borderRadius:12,
                      }}>SESSÃO ATUAL</span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:"var(--gray-500)",marginTop:2}}>
                    <span title={s.ip || ""}>IP: {s.ip || "—"}</span>
                    {" · "}
                    Último acesso: <strong>{fmtRelativo(s.dtUltimoAcesso)}</strong>
                  </div>
                </div>
                {!s.atual && (
                  <button type="button" className="btn btn-outline-danger btn-sm"
                    disabled={loading}
                    onClick={()=>setConfirmRevogar(s)}>
                    <i className="bi bi-x-circle"/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {confirmRevogar === "outras" && (
        <ConfirmModal
          titulo="Encerrar outras sessões?"
          mensagem={`Todos os outros dispositivos serão desconectados (${outras.length}). Sua sessão atual será mantida.`}
          perigo
          textoOk="Sim, encerrar"
          carregando={loading}
          onConfirm={revogarOutras}
          onCancel={()=>setConfirmRevogar(null)}
        />
      )}
      {confirmRevogar && confirmRevogar !== "outras" && (
        <ConfirmModal
          titulo="Encerrar essa sessão?"
          mensagem={`O dispositivo "${confirmRevogar.deviceLabel || "—"}" será desconectado.`}
          perigo
          textoOk="Encerrar"
          carregando={loading}
          onConfirm={()=>revogarUma(confirmRevogar)}
          onCancel={()=>setConfirmRevogar(null)}
        />
      )}
    </div>
  );
}
